import { defineStore } from "pinia";
import { ref } from "vue";
import { Account } from "@/apps/shared/organization/core/domain/Account";
import i18nService from "@/core/composables/i18n.service";

const LOCALSTORAGE_KEY = "cx-account";

const useAccountStore = defineStore("account", () => {
  const storedAccount = window.localStorage.getItem(LOCALSTORAGE_KEY);
  const account = storedAccount ? JSON.parse(storedAccount) : undefined;

  if (account) {
    i18nService.setActiveLanguage(account.language);
    i18nService.setActiveCurrency(account.currency);
  } else {
    // TODO: move next line in webapp/src/core/composables/vue-i18n.js if possible.
    i18nService.setActiveCurrencyToDefault();
  }

  const id = ref<Account["id"] | undefined>(account?.id ?? undefined);
  const name = ref<Account["name"] | undefined>(account?.name ?? undefined);
  const mainColor = ref<Account["mainColor"] | undefined>(account?.mainColor ?? undefined);
  const logo = ref<Account["logo"] | undefined>(account?.logo ?? undefined);
  const language = ref<Account["language"] | undefined>(account?.language ?? undefined);
  const favicon = ref<Account["favicon"] | undefined>(account?.favicon ?? undefined);
  const companyCountry = ref<Account["companyCountry"] | undefined>(account?.companyCountry ?? undefined);
  const currency = ref<Account["currency"] | undefined>(account?.currency ?? undefined);
  const emailDPO = ref<Account["emailDPO"] | undefined>(account?.emailDPO ?? undefined);
  const isBuyerAndWorkshopsSupervisor = ref<Account["isBuyerAndWorkshopsSupervisor"] | undefined>(
    account?.isBuyerAndWorkshopsSupervisor ?? undefined
  );
  const isRetailer = ref<Account["isRetailer"] | undefined>(account?.isRetailer ?? undefined);

  const initFromAccount = (account: Account): void => {
    id.value = account.id;
    name.value = account.name;
    mainColor.value = account.mainColor;
    logo.value = account.logo;
    _setLanguage(account.language);
    favicon.value = account.favicon;
    companyCountry.value = account.companyCountry;
    _setCurrency(account.currency);
    emailDPO.value = account.emailDPO;
    isBuyerAndWorkshopsSupervisor.value = account.isBuyerAndWorkshopsSupervisor;
    isRetailer.value = account.isRetailer;
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(account));
  };

  const isInitialized = (): boolean => {
    return id.value !== undefined;
  };

  const setGeneralSettings = (settings: Pick<Account, "language" | "currency" | "emailDPO">): void => {
    _setLanguage(settings.language);
    _setCurrency(settings.currency);
    emailDPO.value = settings.emailDPO;

    updateStoredAccount(settings);
  };

  const setDesign = (design: Pick<Account, "mainColor" | "logo" | "favicon">): void => {
    mainColor.value = design.mainColor;
    logo.value = design.logo;
    favicon.value = design.favicon;

    updateStoredAccount(design);
  };

  const updateStoredAccount = (partialUpdatedAccount: Partial<Account>) => {
    const storedAccount = window.localStorage.getItem(LOCALSTORAGE_KEY);
    const account = storedAccount ? JSON.parse(storedAccount) : undefined;
    if (account) {
      const updatedAccount = {
        ...account,
        ...partialUpdatedAccount,
      };
      window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(updatedAccount));
    }
  };

  const reset = (): void => {
    id.value = undefined;
    name.value = undefined;
    mainColor.value = undefined;
    logo.value = undefined;
    _setLanguage(undefined);
    favicon.value = undefined;
    companyCountry.value = undefined;
    _setCurrency(undefined);
    emailDPO.value = undefined;
    isBuyerAndWorkshopsSupervisor.value = undefined;
    isRetailer.value = undefined;

    window.localStorage.removeItem(LOCALSTORAGE_KEY);
  };

  const _setLanguage = (newLanguage: string | undefined): void => {
    language.value = newLanguage;
    if (newLanguage) {
      i18nService.setActiveLanguage(newLanguage);
    }
  };

  const _setCurrency = (newCurrency: string | undefined): void => {
    currency.value = newCurrency;
    newCurrency ? i18nService.setActiveCurrency(newCurrency) : i18nService.setActiveCurrencyToDefault();
  };

  return {
    id,
    name,
    mainColor,
    logo,
    language,
    favicon,
    companyCountry,
    currency,
    emailDPO,
    isBuyerAndWorkshopsSupervisor,
    isRetailer,
    initFromAccount,
    isInitialized,
    reset,
    setGeneralSettings,
    setDesign,
  };
});

export default useAccountStore;
